<template>
  <!-- TODO: to adopt content popup usage, duplicated implementation on popperjs -->
  <div ref="annotationToolbar" class="annotation-toolbar" style="display: none">
    <template v-if="annotationActionsEnabled">
      <div
        v-for="(buttonConfig, index) in props.buttonConfigList"
        :key="index"
        class="annotation-tool button"
        :class="generateToolBarClass(buttonConfig)"
        @click="handleClick($event)"
      />
    </template>
    <template v-if="props.showAction">
      <div class="annotation-tool action">
        <!-- Highlight -->
        <cv-link v-if="annotationActionsEnabled" class="highlight" @click="handleHighlight">
          <svg-icon v-if="showUnderlineIcon" name="Underline24" kind="misc" />
          <svg-icon v-else name="Highlight24" kind="action" />
        </cv-link>

        <!-- Highlight with note -->
        <cv-link
          v-if="annotationActionsEnabled"
          class="highlight-with-note"
          @click="handleHighlightWithNote"
        >
          <svg-icon name="Annotate24" kind="action" />
        </cv-link>

        <!-- Record audio -->
        <cv-link v-if="annotationActionsEnabled" class="audio" @click="handleRecord">
          <svg-icon name="Microphone24" kind="action" />
        </cv-link>

        <!-- Drawing -->
        <cv-link v-if="annotationActionsEnabled" class="drawing" @click="handleDraw">
          <svg-icon name="ColorPicker24" kind="richtext" />
        </cv-link>
      </div>
      <div v-if="annotatable && (hasDictionaryAccess || canAskSalis)" class="annotation-divider" />
      <div class="annotation-buttons">
        <!-- Ask Salis -->
        <tooltip-button
          v-if="canAskSalis"
          class="btn-ask-salis"
          kind="ghost"
          :tip="askSalisDisabledTooltipMessage"
          alignment="center"
          direction="bottom"
          :show-tooltip="
            !isAskSalisSupportedComponent ||
            hasReachedAskSalisInteractionLimits ||
            isConversationSuspended
          "
          :disabled="
            !isAskSalisSupportedComponent ||
            hasReachedAskSalisInteractionLimits ||
            isConversationSuspended
          "
          @click="handleAskSalis"
        >
          <svg-icon name="Salis24" kind="action" />
          <span> {{ t("annotation_toolbar_ask_salis_label") }}</span>
        </tooltip-button>

        <!-- E-dictionary -->
        <cv-button
          v-if="annotatable && hasDictionaryAccess"
          class="btn-dictionary"
          kind="ghost"
          @click="handleDefine"
        >
          <svg-icon name="Edictionary24" kind="action" />
          <span> {{ t("annotation_toolbar_e_dictionary_label") }}</span>
        </cv-button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, useTemplateRef } from "vue";
import { createPopper, Instance as PopperInstance } from "@popperjs/core";
import { isEmpty } from "@utils/var.check";
import DOMUtils from "@utils/dom.utils";
import { useAnnotation } from "../composables/annotation.ts";
import { useAnnotationAttemptAdapter } from "@modules/annotation/composables/use-annotation-attempt-adapter.ts";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import { ANNOTATION_ACTION } from "@modules/annotation/service/annotation.constant.ts";
import { CONVERSATION_ACTION } from "@/modules/conversation/services/conversation.constant.ts";
import { AnnotationContext } from "@modules/annotation/types.ts";
import { useI18n } from "vue-i18n";
import { useLessonActivityConversation } from "@modules/attempt/composables/use-lesson-activity-conversation.ts";

interface PopperModifier {
  name: string;
  options?: Record<string, unknown>;
}

interface PopperProps {
  placement?: string;
  modifiers?: PopperModifier[];
}

interface ButtonConfig {
  color: string;
  shape: string;
}

interface Props {
  buttonConfigList: ButtonConfig[];
  showAction?: boolean;
  annotatable?: boolean;
  canAskSalis?: boolean;
  isAskSalisSupportedComponent?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showAction: false,
  annotatable: false,
  canAskSalis: false,
  isAskSalisSupportedComponent: false,
});

const emit = defineEmits<{
  colorAndShapeSelected: [event: Event];
  actionSelected: [action: string];
}>();

const annotationContext = inject<AnnotationContext>("annotationContext");
const { canCreateAnnotations, isEditorContext } = useAnnotationPermissions();

const adapter = useAnnotationAttemptAdapter();
const { setSelectedHighlight, getColorSelector, getSelectedHighlight } = useAnnotation(
  undefined,
  adapter,
);
const { t } = useI18n();

const {
  hasReachedAskSalisInteractionLimits,
  isConversationSuspended,
  getAskSalisUnsupportedComponentTooltip,
} = useLessonActivityConversation();

const annotationToolbar = useTemplateRef("annotationToolbar");
const popperInstance = ref<PopperInstance | null>(null);

const annotationActionsEnabled = computed(() => {
  return props.annotatable && canCreateAnnotations.value;
});

const hasDictionaryAccess = computed(() => {
  return (
    annotationContext &&
    annotationContext.features.dictionary &&
    !isEmpty(annotationContext.dictionarySettings)
  );
});

const isTeacherView = computed(() => {
  return annotationContext?.isTeacherView;
});

const showUnderlineIcon = computed(() => {
  return isTeacherView.value || isEditorContext.value;
});

const askSalisDisabledTooltipMessage = computed(() => {
  if (!props.isAskSalisSupportedComponent) {
    return getAskSalisUnsupportedComponentTooltip();
  } else if (isConversationSuspended.value) {
    return t("conversation_suspended_disabled_ask_salis_tooltip");
  } else if (hasReachedAskSalisInteractionLimits.value) {
    return t("conversation_text_query_toolbar_salis_no_interactions_left_tooltip_message");
  }
  return "";
});

const handleClick = (event: Event) => {
  const target = event.target as HTMLElement;
  setSelectedHighlight(getColorSelector(target.classList));
  emit("colorAndShapeSelected", event);
};

const handleHighlight = () => {
  handleAction(ANNOTATION_ACTION.HIGHLIGHT);
};

const handleHighlightWithNote = () => {
  handleAction(ANNOTATION_ACTION.HIGHLIGHT_WITH_NOTE);
};

const handleRecord = () => {
  handleAction(ANNOTATION_ACTION.RECORD);
};

const handleDraw = () => {
  handleAction(ANNOTATION_ACTION.DRAW);
};

const handleDefine = () => {
  handleAction(ANNOTATION_ACTION.DEFINE);
};

const handleAskSalis = (): void => {
  if (
    !props.isAskSalisSupportedComponent ||
    hasReachedAskSalisInteractionLimits.value ||
    isConversationSuspended.value
  ) {
    return;
  }
  handleAction(CONVERSATION_ACTION.ASK_SALIS);
};

const handleAction = (action: string) => {
  emit("actionSelected", action);
};

const handleAnnotationSelected = (annotation: HTMLElement, popperProps: PopperProps) => {
  /**
   * For props. please read
   * @see https://popper.js.org/docs/v2/modifiers/
   */
  if (annotationToolbar.value) {
    //according to user requirement, always place the floating toolbar at bottom
    popperProps = Object.assign({}, popperProps, { placement: "auto" });
    const popperConfigs = popperProps.modifiers ? popperProps.modifiers : [];
    popperConfigs.push({
      name: "flip",
      options: {
        allowedAutoPlacements: ["bottom"],
      },
    });
    popperProps.modifiers = popperConfigs;
    popperInstance.value = createPopper(annotation, annotationToolbar.value, popperProps);
  }
};

const generateToolBarClass = (buttonConfig: ButtonConfig) => {
  if (props.showAction) {
    // when have actions need to show tick based on color selected
    return [
      `${buttonConfig.color} ${buttonConfig.shape}`,
      { selected: buttonConfig.color === getSelectedHighlight.value },
    ];
  } else {
    return `${buttonConfig.color} ${buttonConfig.shape}`;
  }
};

const destroy = () => {
  if (annotationToolbar.value) {
    DOMUtils.addClass(annotationToolbar.value, "hide");
  }
};

onBeforeUnmount(() => {
  if (popperInstance.value) {
    popperInstance.value.destroy();
  }
});

defineExpose({
  handleAnnotationSelected,
  destroy,
});
</script>

<style lang="scss">
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.annotation-toolbar {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: center;
  background-color: $ui-01;
  padding: $spacing-03;
  margin-top: $spacing-04 !important;
  width: 162px;
  border-radius: 10px;
  box-shadow: 0 0 4px 1px rgba($ui-05, 0.3);
  z-index: z-map("annotation-toolbar");

  // Arrow tip for toolbar
  &::before {
    content: "";
    position: absolute;
    top: -0.5rem;
    border-color: transparent transparent $ui-01 transparent;
    border-width: 0 8px 8px 8px;
    border-style: solid;
    z-index: z-map("default");
  }

  // Pseudo shadow for arrow tip as box-shadow will be rectangular
  &::after {
    content: "";
    position: absolute;
    top: -0.6rem;
    border-color: transparent transparent $ui-05 transparent;
    border-width: 0 9px 9px 9px;
    border-style: solid;
    opacity: 0.1;
  }

  .text-annotation & {
    .button {
      &.shape-2 {
        height: 27px;
        width: 27px;
        border-radius: 50%;
      }
    }
  }

  .image-annotation & {
    .button {
      width: 2rem;
      height: 2rem;
      background-repeat: no-repeat;
      &.shape-1 {
        background: url("/src/assets/images/icons/misc/32px/AnnotateTick32.svg");
      }
      &.shape-2 {
        background: url("/src/assets/images/icons/misc/32px/AnnotateCross32.svg");
      }
      &.shape-3 {
        background: url("/src/assets/images/icons/misc/32px/AnnotateCircle32.svg");
      }
      &.assess-1 {
        background: url("/src/assets/images/icons/misc/32px/AssessmentTick32.svg");
      }
      &.assess-2 {
        background: url("/src/assets/images/icons/misc/32px/AssessmentCross32.svg");
      }
      &.assess-3 {
        background: url("/src/assets/images/icons/misc/32px/AssessmentCircle32.svg");
      }
    }
  }

  .page-template & {
    z-index: 8001; // zindex 1 higher than .bx--side-nav to show annotation on top
  }

  .action {
    flex: 1 1 100%;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    place-items: center;
    gap: $spacing-02 0;
    margin-top: $spacing-03;
    padding-top: $spacing-03;

    &:empty {
      padding: 0;
      margin: 0;

      & + .annotation-buttons {
        margin-top: -$spacing-03;
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
      }
    }

    .sls-icon {
      [stroke] {
        stroke: $interactive-01;
      }
      [fill] {
        fill: $interactive-01;
      }
    }
  }

  .button {
    position: relative;
    margin: 0 $spacing-02;
    border: none;
    cursor: pointer;

    &:focus {
      outline: none;
    }

    &.selected {
      &::before {
        content: "";
        display: inline-block;
        position: absolute;
        top: 4px;
        left: 10px;
        width: 7px;
        height: 14px;
        border-bottom: 2px solid $ui-02;
        border-right: 2px solid $ui-02;
        transform: rotate(45deg);
      }
    }
  }

  .annotation-divider {
    width: 100%;
    margin-top: $spacing-03;
    border-top: 1px solid $ui-04;
  }

  .annotation-buttons {
    width: calc(100% + #{$spacing-05});
    margin: 0 (-$spacing-03) (-$spacing-03);

    &:only-child {
      margin-top: -$spacing-03;

      &:first-child {
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
      }
    }

    .bx--btn {
      width: 100%;
      min-height: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: start;
      padding: $spacing-03;
      border-radius: 0;

      &:disabled {
        opacity: 1;

        span {
          color: $disabled-02;
        }
      }

      &:last-child {
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
      }

      &.tooltip-button {
        .bx--tooltip__trigger {
          padding: 0;

          .bx--assistive-text {
            color: $text-04;
          }
        }
      }

      span {
        @include carbon--type-style("button-01");
        text-transform: uppercase;
      }
    }
  }
}
</style>
